import React, { useEffect, useMemo, useState, useCallback } from "react";
import TableContainer from "../../BaseComponents/BaseTable";
import BaseModal from "../../BaseComponents/BaseModal";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess } from "../../Constant/common";
import { importLeadDataApi } from "../../Api/leadApi";
import { useSelectedCenter } from "../CenterContext";
import {
  Action,
  changeStatus,
  closedDate,
  Company,
  Contact,
  dateLabel,
  Edit,
  editLead,
  followUpDate,
  leadDescription,
  Remarks,
  Save,
  Search,
  status,
  Update,
  updateLeadStatus,
} from "../../Constant";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseSelect from "../../BaseComponents/BaseSelect";
import BaseDatePicker from "../../BaseComponents/BaseDatePicker";
import { listOfConnectedProfileApi, updateLeadStatusApi } from "../../Api/leadApi";
import { listOfLeadApi, createLeadApi, updateLeadApi, viewLead } from "../../Api/leadApi";
import {
  ACCESSOR_KEYS,
  addedBy,
  addLeadLabel,
  allStatuses,
  companyLabel,
  connectedProfile,
  contactNameLabel,
  endDateLabel,
  importExcel,
  importLabel,
  importLeads,
  LEAD_STATUS_BADGE_MAP,
  LEAD_TYPE_OPTIONS,
  leadDescriptionLabel,
  leadLabel,
  leadStatusLabel,
  leadTypeLabel,
  resetLabel,
  selectExcel,
  startDateLabel,
  truncateText,
} from "../../Constant/Lead";
import BaseInput from "../../BaseComponents/BaseInput";
import { listOfCompanyApi } from "../../Api/companyApi";
import { listOfContactApi } from "../../Api/contactApi";
import * as yup from "yup";
import { useFormik } from "formik";
import { SelectPlaceHolder, validationMessages } from "../../Constant/validation";
import { listOfSchedulerwApi } from "../../Api/interviewApi";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import {
  Plus,
  Upload,
  RotateCcw,
  Edit2,
  TrendingUp,
  Users,
  Briefcase,
  AlertCircle,
  Filter,
} from "react-feather";
import BaseTooltip from "../../BaseComponents/BaseTooltip";

/* ── Status badge config ── */
const STATUS_CFG = {
  Cold: { color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
  Hot: { color: "#06b6d4", bg: "rgba(6,182,212,0.12)" },
  "Very Hot": { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  "Follow-Up": { color: "#f43f5e", bg: "rgba(244,63,94,0.12)" },
};

const leadValidationSchema = yup.object({
  company_id: yup.number().required(validationMessages.required(Company)),
  contact_id: yup.number().required(validationMessages.required(Contact)),
  profile_id: yup.number().required(validationMessages.required(connectedProfile)),
  lead_description: yup.string().required(validationMessages.required(leadDescription)),
  date: yup.date().required(validationMessages.required(dateLabel)),
  lead_type: yup.string().required(validationMessages.required(leadTypeLabel)),
});

const Lead = () => {
  document.title = leadLabel;
  const { setLoading } = usePageLoader();
  const { selectedCenterPayload } = useSelectedCenter();

  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(5);
  const [searchValue, setSearchValue] = useState("");
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [leadData, setLeadData] = useState([]);
  const [leadSearchData, setLeadSearchData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [contactList, setContactList] = useState([]);
  const [profileList, setProfileList] = useState([]);
  const [addedByList, setAddedByList] = useState([]);
  const [loading, setModalLoading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [showFilters, setShowFilters] = useState(true);

  const INITIAL_FILTERS = { startDate: "", endDate: "", lead_status: "" };
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [statusFormData, setStatusFormData] = useState({
    id: null,
    lead_status: "",
    lead_remarks: "",
    closed_date: new Date().toISOString().split("T")[0],
  });

  const [formData] = useState({
    company_id: "",
    contact_id: "",
    profile_id: "",
    lead_description: "",
    date: new Date().toISOString().split("T")[0],
    follow_up_date: null,
    lead_type: "",
    added_by: "",
  });

  /* ── Formik ── */
  const leadFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      company_id: formData.company_id || "",
      contact_id: formData.contact_id || "",
      profile_id: formData.profile_id || "",
      lead_description: formData.lead_description || "",
      date: formData.date || new Date().toISOString().split("T")[0],
      follow_up_date: formData.follow_up_date || null,
      lead_type: formData.lead_type || "",
      added_by: formData.added_by || "",
    },
    validationSchema: leadValidationSchema,
    onSubmit: async (values) => {
      setModalLoading(true);
      try {
        const payload = {
          ...values,
          company_id: Number(values.company_id),
          contact_id: Number(values.contact_id),
          connection_profile_id: Number(values.profile_id),
          added_by: Number(values.added_by),
        };
        const response =
          isEditMode && selectedLeadId
            ? await updateLeadApi({ ...payload, id: selectedLeadId })
            : await createLeadApi(payload);
        if (checkStatusCodeSuccess(response?.statusCode)) {
          toast.success(response?.message);
          setShowAddModal(false);
          searchValue ? fetchSearchLeadPermission() : fetchLeadPermission();
        } else throw new Error(response?.message);
      } catch (error) {
        toast.error(error?.response?.data?.message || error?.message);
      } finally {
        setModalLoading(false);
      }
    },
  });

  /* ── Fetch helpers ── */
  const fetchCompanies = useCallback(async () => {
    try {
      const r = await listOfCompanyApi({});
      if (checkStatusCodeSuccess(r?.statusCode))
        setCompanyList((r?.data || []).map((c) => ({ value: c.id, label: c.company_name })));
    } catch (e) {
      return e;
    }
  }, []);
  const fetchContacts = useCallback(async () => {
    try {
      const r = await listOfContactApi({});
      if (checkStatusCodeSuccess(r?.statusCode))
        setContactList((r?.data || []).map((c) => ({ value: c.id, label: c.contact_name })));
    } catch (e) {
      return e;
    }
  }, []);
  const fetchConnectedProfile = useCallback(async () => {
    try {
      const r = await listOfConnectedProfileApi({});
      if (checkStatusCodeSuccess(r?.statusCode))
        setProfileList((r?.data || []).map((p) => ({ value: p.id, label: p.profile_name })));
    } catch (e) {
      toast.error(e?.response?.data?.message);
    }
  }, []);
  const fetchaddedBy = useCallback(async () => {
    try {
      const r = await listOfSchedulerwApi({ center_id: selectedCenterPayload.center_id });
      if (checkStatusCodeSuccess(r?.statusCode))
        setAddedByList((r?.data || []).map((a) => ({ value: a.id, label: a.name })));
    } catch (e) {
      toast.error(e?.response?.data?.message);
    }
  }, [selectedCenterPayload.center_id]);

  const openAddModal = () => {
    setIsEditMode(false);
    setSelectedLeadId(null);
    leadFormik.resetForm();
    fetchCompanies();
    fetchContacts();
    fetchConnectedProfile();
    fetchaddedBy();
    setShowAddModal(true);
  };

  const handleEditLead = useCallback(
    async (id) => {
      try {
        setModalLoading(true);
        const response = await viewLead(id);
        if (response?.message) {
          const d = response.message;
          setSelectedLeadId(id);
          setIsEditMode(true);
          leadFormik.setValues({
            company_id: d.company.id || "",
            contact_id: d.contact.id || "",
            profile_id: d.connectionProfile.id || "",
            lead_description: d.lead_description || "",
            date: d.date || new Date().toISOString().split("T")[0],
            follow_up_date: d.follow_up_date || null,
            lead_type: d.lead_type || "",
            added_by: d.authUser.id || "",
          });
          await Promise.all([
            fetchCompanies(),
            fetchContacts(),
            fetchConnectedProfile(),
            fetchaddedBy(),
          ]);
          setShowAddModal(true);
        } else toast.error(response?.message);
      } catch (e) {
        toast.error(e?.message);
      } finally {
        setModalLoading(false);
      }
    },
    [fetchCompanies, fetchContacts, fetchConnectedProfile, fetchaddedBy, leadFormik],
  );

  /* ── Lead list fetch ── */
  const buildPayload = useCallback(
    (withSearch) => ({
      order: [columnName, sortOrder],
      pageNumber: currentPage,
      pageSize: customPageSize,
      ...(withSearch && { search: searchValue }),
      ...(filters.startDate && { startDate: filters.startDate }),
      ...(filters.endDate && { endDate: filters.endDate }),
      ...(filters.lead_status && { condition: { lead_status: filters.lead_status } }),
    }),
    [columnName, sortOrder, currentPage, customPageSize, searchValue, filters],
  );

  const fetchSearchLeadPermission = useCallback(() => {
    setLoading(true);
    listOfLeadApi(buildPayload(true))
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setLeadSearchData(resp?.data?.listOfLead);
          setTotalRecords(resp?.data?.totalRecordsCount);
          setTotalPages(resp?.data?.totalPages);
          setTotalNumberOfRows(resp?.data?.numberOfRows);
          setCurrentPage(resp?.data?.currentPage);
        } else setLeadSearchData([]);
      })
      .catch(() => setLeadSearchData([]))
      .finally(() => setLoading(false));
  }, [buildPayload, setLoading]);

  const fetchLeadPermission = useCallback(() => {
    setLoading(true);
    listOfLeadApi(buildPayload(false))
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setLeadData(resp?.data?.listOfLead);
          setTotalRecords(resp?.data?.totalRecordsCount);
          setTotalPages(resp?.data?.totalPages);
          setTotalNumberOfRows(resp?.data?.numberOfRows);
          setCurrentPage(resp?.data?.currentPage);
          if (searchValue) fetchSearchLeadPermission();
        }
      })
      .catch(() => setLeadData([]))
      .finally(() => setLoading(false));
  }, [buildPayload, searchValue, fetchSearchLeadPermission, setLoading]);

  useEffect(() => {
    searchValue ? fetchSearchLeadPermission() : fetchLeadPermission();
  }, [
    currentPage,
    customPageSize,
    sortOrder,
    columnName,
    searchValue,
    filters,
    fetchLeadPermission,
    fetchSearchLeadPermission,
  ]);

  /* ── Pagination ── */
  const handleFetchData = (page) => setCurrentPage(page);
  const handleSearchValueChange = (value) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };
  const handleFetchSorting = (page, id, order) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  /* ── Filters ── */
  const hasActiveFilters = useMemo(
    () =>
      Boolean(filters.startDate || filters.endDate || filters.lead_status || searchValue?.trim()),
    [filters, searchValue],
  );
  const handleFilterReset = () => {
    setFilters(INITIAL_FILTERS);
    setSearchValue("");
    setCurrentPage(1);
    setCustomPageSize(5);
  };

  /* ── Status change ── */
  const handleStatusChangeClick = useCallback((lead) => {
    setStatusFormData({
      id: lead.id,
      lead_status: lead.lead_status || "",
      lead_remarks: lead.lead_remarks || "",
      closed_date: new Date().toISOString().split("T")[0],
    });
    setShowStatusModal(true);
  }, []);

  const handleStatusSubmit = async () => {
    setModalLoading(true);
    try {
      const response = await updateLeadStatusApi(statusFormData);
      if (checkStatusCodeSuccess(response?.statusCode)) {
        toast.success(response?.message);
        setShowStatusModal(false);
        searchValue ? fetchSearchLeadPermission() : fetchLeadPermission();
      } else throw new Error(response?.message);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setModalLoading(false);
    }
  };

  /* ── Import ── */
  const handleImportSubmit = async () => {
    if (!selectedFile) return;
    const fd = new FormData();
    fd.append("file", selectedFile);
    try {
      setIsImporting(true);
      const response = await importLeadDataApi(fd);
      if (checkStatusCodeSuccess(response?.statusCode)) {
        toast.success(response.message);
        setShowImportModal(false);
        searchValue ? fetchSearchLeadPermission() : fetchLeadPermission();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message);
    } finally {
      setIsImporting(false);
      setSelectedFile(null);
      setFileInputKey(Date.now());
    }
  };

  /* ── Columns ── */
  const columns = useMemo(
    () => [
      {
        header: ACCESSOR_KEYS.SRNOLABEL,
        accessorKey: ACCESSOR_KEYS.SERIAL_NUMBER,
        cell: (c) => (currentPage - 1) * customPageSize + c.row.index + 1,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: companyLabel,
        accessorKey: ACCESSOR_KEYS.COMPANY,
        cell: ({ row }) => {
          const name = row?.original?.company?.company_name;
          const truncated = truncateText(name, 25);
          return name && name.length > 25 ? (
            <BaseTooltip label={name} position="top" color="#6366f1">
              <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>
                {truncated}
              </span>
            </BaseTooltip>
          ) : (
            <span>{name || "—"}</span>
          );
        },
        enableColumnFilter: false,
      },
      {
        header: contactNameLabel,
        accessorKey: ACCESSOR_KEYS.CONTACT_NAME,
        cell: ({ row }) => row?.original?.contact?.contact_name || "—",
        enableColumnFilter: false,
        enableSorting: false,
      },
      { header: leadTypeLabel, accessorKey: ACCESSOR_KEYS.LEAD_TYPE, enableColumnFilter: false },
      {
        header: leadDescriptionLabel,
        accessorKey: ACCESSOR_KEYS.LEAD_DESCRIPTION,
        cell: (c) => {
          const desc = c.row.original?.lead_description;
          const truncated = truncateText(desc, 30);
          const isTruncated = desc && desc.length > 30;
          return isTruncated ? (
            <BaseTooltip label={desc} position="top" maxWidth={280} color="#6366f1">
              <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>
                {truncated}
              </span>
            </BaseTooltip>
          ) : (
            <span>{truncated}</span>
          );
        },
        enableColumnFilter: false,
      },
      { header: dateLabel, accessorKey: ACCESSOR_KEYS.DATE, enableColumnFilter: false },
      {
        header: leadStatusLabel,
        accessorKey: ACCESSOR_KEYS.LEAD_STATUS,
        enableColumnFilter: false,
        enableSorting: false,
        cell: (c) => {
          const s = c.row.original?.lead_status;
          const cfg = STATUS_CFG[s] || { color: "#64748b", bg: "rgba(100,116,139,0.12)" };
          return (
            <span
              style={{
                padding: "3px 10px",
                borderRadius: 999,
                fontSize: "0.6875rem",
                fontWeight: 700,
                background: cfg.bg,
                color: cfg.color,
                whiteSpace: "nowrap",
              }}
            >
              {s || "—"}
            </span>
          );
        },
      },
      {
        header: Action,
        accessorKey: ACCESSOR_KEYS.ACTION,
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <BaseTooltip label={changeStatus} color="#047857">
              <button
                onClick={() => handleStatusChangeClick(row.original)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(16,185,129,0.08)",
                  border: "1px solid rgba(16,185,129,0.2)",
                  color: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <TrendingUp size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
            <BaseTooltip label={Edit} color="#b45309">
              <button
                onClick={() => handleEditLead(row.original.id)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.2)",
                  color: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Edit2 size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
          </div>
        ),
      },
    ],
    [currentPage, customPageSize, handleEditLead, handleStatusChangeClick],
  );

  /* ── JSX ── */
  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(99,102,241,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Briefcase size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {leadLabel}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0 ? `${totalRecords} leads` : "Manage your sales leads"}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={() => setShowFilters((f) => !f)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 10,
              border: `1px solid ${showFilters || hasActiveFilters ? "rgba(99,102,241,0.3)" : "var(--glass-border)"}`,
              background:
                showFilters || hasActiveFilters ? "rgba(99,102,241,0.1)" : "var(--icon-btn-bg)",
              color:
                showFilters || hasActiveFilters ? "var(--brand-indigo)" : "var(--text-secondary)",
              fontSize: "0.8125rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Filter size={14} strokeWidth={2} />
            Filters{" "}
            {hasActiveFilters && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "var(--brand-indigo)",
                  display: "inline-block",
                }}
              />
            )}
          </button>
          <BaseButton
            color="secondary"
            onClick={() => setShowImportModal(true)}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Upload size={14} strokeWidth={2} /> {importExcel}
          </BaseButton>
          <BaseButton
            color="primary"
            onClick={openAddModal}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={15} strokeWidth={2.5} /> {addLeadLabel}
          </BaseButton>
        </div>
      </div>

      {/* ── Filter panel ── */}
      {showFilters && (
        <div
          className="animate-slide-down"
          style={{
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 16,
            padding: "16px 20px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 16,
              alignItems: "end",
            }}
          >
            <BaseDatePicker
              name="startDate"
              label={startDateLabel}
              value={filters.startDate}
              onChange={(v) => setFilters((p) => ({ ...p, startDate: v }))}
              maxDate={filters.endDate ? new Date(filters.endDate) : undefined}
              placeholder="Select start date"
            />
            <BaseDatePicker
              name="endDate"
              label={endDateLabel}
              value={filters.endDate}
              onChange={(v) => setFilters((p) => ({ ...p, endDate: v }))}
              minDate={filters.startDate ? new Date(filters.startDate) : undefined}
              placeholder="Select end date"
            />
            <BaseSelect
              name="lead_status"
              label={leadStatusLabel}
              value={filters.lead_status}
              placeholder={SelectPlaceHolder(leadStatusLabel)}
              handleChange={(name, value) => setFilters((p) => ({ ...p, [name]: value }))}
              options={[
                { value: "", label: allStatuses },
                ...Object.keys(LEAD_STATUS_BADGE_MAP).map((s) => ({ value: s, label: s })),
              ]}
            />
            <div style={{ marginBottom: 16 }}>
              <button
                onClick={handleFilterReset}
                disabled={!hasActiveFilters}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 9,
                  width: "100%",
                  justifyContent: "center",
                  border: "1px solid rgba(244,63,94,0.3)",
                  background: hasActiveFilters ? "rgba(244,63,94,0.08)" : "var(--icon-btn-bg)",
                  color: hasActiveFilters ? "#f43f5e" : "var(--text-muted)",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: hasActiveFilters ? "pointer" : "not-allowed",
                  opacity: hasActiveFilters ? 1 : 0.5,
                }}
              >
                <RotateCcw size={14} strokeWidth={2} /> {resetLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {leadData?.length > 0 || leadSearchData?.length > 0 ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            fetchData={handleFetchData}
            customPageSize={customPageSize}
            setCustomPageSize={setCustomPageSize}
            columns={columns}
            shouldLineBreak={false}
            data={searchValue ? leadSearchData : leadData || []}
            onSearch={handleSearchValueChange}
            fetchSortingData={handleFetchSorting}
            SearchPlaceholder={Search}
            isGlobalFilter={true}
            manualPagination={true}
            manualFiltering={true}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>No leads found</p>
          </div>
        )}
      </div>

      {/* ── Status change modal ── */}
      <BaseModal
        isOpen={showStatusModal}
        toggler={() => setShowStatusModal(false)}
        title={updateLeadStatus}
        size="md"
        submit={handleStatusSubmit}
        loader={loading}
        submitText={Update}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <BaseSelect
            label={status}
            name="lead_status"
            value={statusFormData.lead_status}
            options={Object.keys(LEAD_STATUS_BADGE_MAP).map((s) => ({ value: s, label: s }))}
            handleChange={(name, value) => setStatusFormData((p) => ({ ...p, [name]: value }))}
            placeholder={SelectPlaceHolder(status)}
            required
          />
          <BaseInput
            label={Remarks}
            type="textarea"
            name="lead_remarks"
            value={statusFormData.lead_remarks}
            handleChange={(e) => setStatusFormData((p) => ({ ...p, lead_remarks: e.target.value }))}
          />
          <BaseDatePicker
            label={closedDate}
            name="closed_date"
            value={statusFormData.closed_date}
            onChange={(v) => setStatusFormData((p) => ({ ...p, closed_date: v }))}
            required
          />
        </div>
      </BaseModal>

      {/* ── Add / Edit lead modal ── */}
      <BaseModal
        isOpen={showAddModal}
        toggler={() => {
          if (!loading) {
            leadFormik.resetForm();
            setIsEditMode(false);
            setSelectedLeadId(null);
            setShowAddModal(false);
          }
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(99,102,241,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isEditMode ? (
                <Edit2 size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              ) : (
                <Plus size={14} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
              )}
            </div>
            <span>{isEditMode ? editLead : addLeadLabel}</span>
          </div>
        }
        submitText={isEditMode ? Update : Save}
        disabled={loading}
        loader={loading}
        submit={leadFormik.handleSubmit}
        size="lg"
      >
        <form onSubmit={leadFormik.handleSubmit} noValidate>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: "0 16px",
            }}
          >
            <BaseSelect
              id="company_id"
              name="company_id"
              label={companyLabel}
              options={companyList}
              value={leadFormik.values.company_id}
              touched={leadFormik.touched.company_id}
              handleChange={(name, value) => leadFormik.setFieldValue(name, value)}
              handleBlur={leadFormik.handleBlur}
              error={leadFormik.touched.company_id && leadFormik.errors.company_id}
              required
            />
            <BaseSelect
              id="contact_id"
              name="contact_id"
              label={contactNameLabel}
              options={contactList}
              value={leadFormik.values.contact_id}
              handleChange={(name, value) => leadFormik.setFieldValue(name, value)}
              handleBlur={leadFormik.handleBlur}
              touched={leadFormik.touched.contact_id}
              error={leadFormik.touched.contact_id && leadFormik.errors.contact_id}
              required
            />
            <BaseSelect
              id="profile_id"
              name="profile_id"
              label={connectedProfile}
              options={profileList}
              value={leadFormik.values.profile_id}
              handleChange={(name, value) => leadFormik.setFieldValue(name, value)}
              handleBlur={leadFormik.handleBlur}
              touched={leadFormik.touched.profile_id}
              error={leadFormik.touched.profile_id && leadFormik.errors.profile_id}
              required
            />
            <BaseSelect
              id="lead_type"
              name="lead_type"
              label={leadTypeLabel}
              options={LEAD_TYPE_OPTIONS}
              value={leadFormik.values.lead_type}
              handleChange={(name, value) => leadFormik.setFieldValue(name, value)}
              handleBlur={leadFormik.handleBlur}
              touched={leadFormik.touched.lead_type}
              error={leadFormik.touched.lead_type && leadFormik.errors.lead_type}
              required
            />
            <BaseSelect
              id="added_by"
              name="added_by"
              label={addedBy}
              options={addedByList}
              value={leadFormik.values.added_by}
              handleChange={(name, value) => leadFormik.setFieldValue(name, value)}
              handleBlur={leadFormik.handleBlur}
              touched={leadFormik.touched.added_by}
              error={leadFormik.touched.added_by && leadFormik.errors.added_by}
            />
            <BaseDatePicker
              name="date"
              label={dateLabel}
              value={leadFormik.values.date}
              onChange={(v) => leadFormik.setFieldValue("date", v)}
              onBlur={leadFormik.handleBlur}
              touched={leadFormik.touched.date}
              error={leadFormik.touched.date && leadFormik.errors.date}
              required
            />
            <BaseDatePicker
              name="follow_up_date"
              label={followUpDate}
              value={leadFormik.values.follow_up_date}
              onChange={(v) => leadFormik.setFieldValue("follow_up_date", v)}
              onBlur={leadFormik.handleBlur}
            />
            <div style={{ gridColumn: "1 / -1" }}>
              <BaseInput
                label={leadDescription}
                type="textarea"
                id="lead_description"
                name="lead_description"
                rows="3"
                value={leadFormik.values.lead_description}
                handleChange={leadFormik.handleChange}
                handleBlur={leadFormik.handleBlur}
                touched={leadFormik.touched.lead_description}
                error={leadFormik.touched.lead_description && leadFormik.errors.lead_description}
                required
              />
            </div>
          </div>
        </form>
      </BaseModal>

      {/* ── Import modal ── */}
      <BaseModal
        isOpen={showImportModal}
        toggler={() => setShowImportModal(false)}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(16,185,129,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Upload size={14} strokeWidth={2} style={{ color: "#10b981" }} />
            </div>
            <span>{importLeads}</span>
          </div>
        }
        submit={handleImportSubmit}
        submitText={importLabel}
        disabled={!selectedFile || isImporting}
        loader={isImporting}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div
            style={{
              padding: "12px 16px",
              borderRadius: 10,
              background: "rgba(99,102,241,0.06)",
              border: "1px solid rgba(99,102,241,0.15)",
              fontSize: "0.8125rem",
              color: "var(--text-muted)",
            }}
          >
            <Users
              size={14}
              strokeWidth={2}
              style={{ color: "var(--brand-indigo)", marginRight: 6 }}
            />
            Accepted formats:{" "}
            <strong style={{ color: "var(--text-primary)" }}>.xlsx, .xls, .csv</strong>
          </div>
          <BaseInput
            key={fileInputKey}
            type="file"
            label={selectExcel}
            id="fileInput"
            accept=".xlsx, .xls, .csv"
            handleChange={(e) => setSelectedFile(e.target.files[0])}
            disabled={isImporting}
          />
        </div>
      </BaseModal>
    </div>
  );
};

export default Lead;
